import {ValidarCPF} from "@/schemas/validation/ValidarCPF";
import {CreditCard} from "@/schemas/creditCard/CreditCard";
import { IAllStates, Recaptcha } from "@/store/slices/appSlice";
import Nullable from "@/types/utils/Nullable";
import { IDebito } from "@/interfaces/PaymentInterface";
import { BraintreeTokenizePayload } from "@paypal/react-paypal-js/dist/types/types/braintree/commonsTypes";
import Apm from "../Apm";
import loadDataset from "@/utils/load-dataset";
import { SliceAddress } from "@/store/slices/userSlice";
import { formatCEP } from "../validation";

type Documento = 'pf' | 'pj'

interface ICobranca {
    logradouro?: string;
    numero?: number;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    uf?: string;
    pais?: string;
    cep?: string;
    dica?: string;
    referencia?: string;
    id_grupo_escritorio_venda?: number;
}
interface IResponsavel {
    tipo_documento: Documento,
    nome?: string,
    sobrenome?: string,
    celular?: string,
    documento?: string,
    email: string
}
export interface IRetorno{
    recaptcha: Recaptcha,
    oferta?: string,
    ztermCode?: string,
    tipo_oferta: Nullable<string>,
    canal_venda: string,
    is_negative_option?: boolean,
    tipo_publicidade: string,
    url: string,
    id_transacao?: number,
    responsavel: IResponsavel,
    dataset?: {[key: string]: string|number}
    pagamento: {
        valor: number,
        forma: string,
        parcela:string,
        cartao?: {},
        debito?: IDebito,
        paypal: undefined,
        cortesia: undefined,
        prepago: undefined,
        voucher: undefined,
    },
    referrer_url: string,
    cobranca?: ICobranca,
    recebedor?: IResponsavel
}

interface IDados {
    documento: string
    telefone: string
}

export interface IPagamento {
    parcela:string
    telefone: string
    titular: string
    documento: string
    numero: string
    cvv: string
    validade: string
}

type Data = null | IDados | BraintreeTokenizePayload | IPagamento

type PaymentMethod = 'cc' | 'dc' | 'paypal' | 'pre-pago' | 'cortesia'

export class PaymentTransform {

    private readonly states: any
    private readonly paymentMethod: string
    private readonly url: URL;
    private readonly data: any;

    constructor(_states: IAllStates, _paymentMethod: PaymentMethod, _url: URL, data: Data = null) {
        this.paymentMethod = _paymentMethod;
        this.states = _states;
        this.url = _url;
        this.data = data;
    }

    private default(): IRetorno | null {
        const state: IAllStates = this.states;
        const tipo_documento: Documento = ValidarCPF(state.payment.value.documento.replace(/\D/g, '')) ? 'pf' : 'pj';

        if(!state.offer.value.valor){
            Apm.captureError('Erro no arquivo utils/transform/payment.ts state.offer.value.valor ainda não tinha sido inicializado')
            return null
        }
        let valor: number = state?.offer?.value?.valor?.valor
        if (state.offer.value.is_escadinha) {
            valor = state.offer.value.valor.original
        }
        let tipoPublicidade = 'WEB'
        if (this.url.searchParams.has('canal') && this.url.searchParams.get('canal') ==='PAY') {
            tipoPublicidade = 'PAY'
        }
        const cep = state.usuario.value.address?.cep 
        const cepPayment = cep ? formatCEP(cep) : cep
        const dataset = loadDataset()

        const cobranca = {
            logradouro: state.usuario.value.address?.logradouro,
            numero: state.usuario.value.address?.numero,
            complemento: state.usuario.value.address?.complemento,
            bairro: state.usuario.value.address?.bairro,
            cidade: state.usuario.value.address?.cidade,
            uf: state.usuario.value.address?.uf,
            cep: cepPayment,
            dica: state.usuario.value.address?.dica_entrega,
            referencia: state.usuario.value.address?.ponto_ref,
            id_grupo_escritorio_venda: state.usuario.value.address?.escritorio
        }

        const retorno: IRetorno = {
            
            recaptcha: state.app.value.recaptcha,
            oferta: state.offer.value.cd_oferta,
            is_negative_option: state.offer.value.is_negative_option,
            ztermCode: state.offer.value.ztermCode,
            tipo_oferta: state.offer.value.offer_type === 'impresso_digital' ? 'impresso' : state.offer.value.offer_type,
            canal_venda: 'checkout-web',
            tipo_publicidade: tipoPublicidade,
            url: this.url.href.toString(),
            id_transacao: state.offer.value.id_transacao,
            responsavel: {
                tipo_documento: tipo_documento,
                nome: state.usuario.value.nome,
                sobrenome: state.usuario.value.sobre_nome,
                celular: state.usuario.value.telefone,
                documento: state.usuario.value.documento?.replace(/\D/g, ''),
                email: state.usuario.value.email
            },
            pagamento: {
                valor: valor,
                forma: '',
                parcela:'',
                cartao: undefined,
                debito: undefined,
                paypal: undefined,
                cortesia: undefined,
                prepago: undefined,
                voucher: undefined,
            },
            dataset: dataset,
            referrer_url: '',

        }

        const url_checkout = new URLSearchParams(this.url.href);
        if (url_checkout.get('referrer_url')) {
            // @ts-ignore
            retorno.referrer_url = url_checkout.get('referrer_url').toString().replace(/ /g, '+')
        }
        switch (this.paymentMethod) {
            case 'cc':
                retorno.responsavel.celular = this.data.telefone
                if (state.usuario.value.sobre_nome != null) {
                    retorno.responsavel.sobrenome = state.usuario.value.sobre_nome
                }
                retorno.pagamento.forma = 'cc'
                retorno.pagamento.parcela = this.data.parcela
                const number = this.data.numero.replace(/\D/g, '').trim()
                retorno.pagamento.cartao = {
                    numero: number,
                    cvv: this.data.cvv,
                    documento: this.data.documento.replace(/\D/g, ''),
                    titular: this.data.titular,
                    validade: this.data.validade,
                    bandeira: new CreditCard(this.data.numero).getBrand()
                }

                retorno.cobranca = cobranca;
                retorno.recebedor = retorno.responsavel;
                retorno.responsavel.documento = this.data.documento;
                retorno.responsavel.tipo_documento = ValidarCPF(this.data.documento.replace(/\D/g, '')) ? 'pf' : 'pj';
                break;
                
            case 'dc':
                retorno.pagamento.forma = 'dc'
                retorno.responsavel.celular = this.data.telefone
                if (state.usuario.value.sobre_nome != null) {
                    retorno.responsavel.sobrenome = state.usuario.value.sobre_nome
                }
                retorno.pagamento.debito = {
                    banco: this.data.banco,
                    digito: this.data.digito,
                    agencia: this.data.agencia,
                    agencia_digito: this.data.agencia_digito,
                    dia: this.data.dia,
                    numero: this.data.numero,
                    titular: this.data.titular,
                    documento: this.data.documento?.replace(/\D/g, ''),
                }

                retorno.responsavel.documento = this.data.documento;
                retorno.responsavel.tipo_documento = ValidarCPF(this.data.documento.replace(/\D/g, '')) ? 'pf' : 'pj';
                retorno.cobranca = cobranca;
                retorno.recebedor = retorno.responsavel;
                break;
            case 'paypal':
                retorno.pagamento.forma = 'paypal'
                retorno.pagamento.paypal = this.data
                break;
            case 'pre-pago':
                retorno.pagamento.valor = 0
                retorno.pagamento.forma = 'pre-pago'
                retorno.responsavel.documento = this.data.documento.replace(/\D/g, '')
                retorno.responsavel.tipo_documento = ValidarCPF(this.data.documento.replace(/\D/g, '')) ? 'pf' : 'pj';
                retorno.responsavel.celular = this.data.telefone
                retorno.pagamento.voucher = this.states.payment.value.voucher
                break;
            case 'cortesia':
                retorno.pagamento.forma = 'cortesia'
                retorno.responsavel.documento = this.data.documento.replace(/\D/g, '')
                retorno.responsavel.tipo_documento = ValidarCPF(this.data.documento.replace(/\D/g, '')) ? 'pf' : 'pj';
                retorno.responsavel.celular = this.data.telefone
                break;
        }
        return retorno
    }

    process() {
        switch (this.states.offer.value.offer_type) {
            case 'digital_completa':
            case 'digital_basica':
            case 'impresso_digital':
                return this.default()
        }
    }

}
